import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PS } from '../theme';
import { Icon } from '../lib/icons';
import { thb } from '../lib/format';
import Screen from '../components/Screen';
import { useFetch } from '../lib/useFetch';
import api from '../lib/api';
import { BACKEND_URL } from '../lib/api';
import { enqueueEvidence } from '../lib/sync';
import { useOnline } from '../lib/useOnline';
import { notifyError, notifyInfo } from '../lib/toast';

const FILTERS = [
  { label: 'ทั้งหมด', types: null },
  { label: 'จ่ายเงิน', types: ['payment_recorded', 'settle'] },
  { label: 'ประมูล', types: ['bid_opened', 'bid_placed', 'bid_won'] },
  { label: 'ลงนาม', types: ['contract_signed'] },
  { label: 'เจอหน้า', types: ['meetup_checkin'] },
  { label: 'ข้อความ', types: ['chat_message'] },
];

function AddEvidence({ circleId, onClose, onDone }) {
  const [title, setTitle] = useState('');
  const [type, setType] = useState('payment_recorded');
  const [meta, setMeta] = useState('');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const online = useOnline();
  const submit = async () => {
    if (!title) return;
    setBusy(true);
    const queueIt = () => enqueueEvidence({ circle_id: circleId, event_type: type, title, meta, fileBlob: file || null, fileName: file?.name });
    try {
      if (!navigator.onLine) {
        await queueIt();
        onDone({ queued: true });
        return;
      }
      let fileId = null;
      if (file) {
        const fd = new FormData(); fd.append('file', file);
        const up = await api.post('/files/upload', fd);
        fileId = up.data.file_id;
      }
      await api.post('/evidence', { circle_id: circleId, event_type: type, title, meta, payload: {}, file_id: fileId });
      onDone({ queued: false });
    } catch (e) {
      // network failure mid-request → keep it safe in the offline queue
      if (!navigator.onLine || !e?.response) { await queueIt(); onDone({ queued: true }); }
      else { notifyError(e?.response?.data?.detail || 'บันทึกไม่สำเร็จ'); }
    } finally { setBusy(false); }
  };
  return (
    <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(20,18,16,0.45)', display: 'flex', alignItems: 'flex-end', zIndex: 20 }}>
      <div onClick={e => e.stopPropagation()} className="ps-fade-up" style={{ width: '100%', background: PS.surface2, borderRadius: '22px 22px 0 0', padding: 20 }}>
        <div style={{ width: 40, height: 4, borderRadius: 2, background: PS.line, margin: '0 auto 16px' }} />
        <div style={{ fontSize: 17, fontWeight: 600, marginBottom: 14 }}>เพิ่มหลักฐานใหม่</div>
        <div style={{ fontSize: 11, color: PS.textMuted, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6 }}>ประเภท</div>
        <select data-testid="add-ev-type" value={type} onChange={e => setType(e.target.value)} style={{ width: '100%', height: 44, borderRadius: 12, border: `1px solid ${PS.line}`, padding: '0 12px', fontSize: 14, fontFamily: PS.fontUI, background: PS.surface, marginBottom: 12 }}>
          <option value="payment_recorded">การจ่ายเงิน</option>
          <option value="bid_won">ผลการประมูล</option>
          <option value="meetup_checkin">นัดเจอหน้า</option>
          <option value="contract_signed">เอกสาร/สัญญา</option>
        </select>
        <div style={{ fontSize: 11, color: PS.textMuted, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6 }}>หัวข้อ</div>
        <input data-testid="add-ev-title" value={title} onChange={e => setTitle(e.target.value)} placeholder="เช่น โอนเงินงวด 4" style={{ width: '100%', height: 44, borderRadius: 12, border: `1px solid ${PS.line}`, padding: '0 12px', fontSize: 14, fontFamily: PS.fontUI, marginBottom: 12, outline: 'none' }} />
        <div style={{ fontSize: 11, color: PS.textMuted, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6 }}>รายละเอียด</div>
        <input data-testid="add-ev-meta" value={meta} onChange={e => setMeta(e.target.value)} placeholder="เพิ่มเติม (ถ้ามี)" style={{ width: '100%', height: 44, borderRadius: 12, border: `1px solid ${PS.line}`, padding: '0 12px', fontSize: 14, fontFamily: PS.fontUI, marginBottom: 12, outline: 'none' }} />
        <label data-testid="add-ev-file-label" style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '11px 14px', borderRadius: 12, border: `1px dashed ${PS.line}`, marginBottom: 16, cursor: 'pointer', background: file ? 'oklch(0.78 0.06 155 / 0.18)' : PS.surface }}>
          <Icon.doc size={16} stroke={PS.ink} />
          <span style={{ flex: 1, fontSize: 12.5, color: file ? PS.text : PS.textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{file ? file.name : 'แนบรูป/ไฟล์หลักฐาน (ถ้ามี)'}</span>
          {file && <Icon.check size={14} stroke={PS.ok} sw={2.5} />}
          <input data-testid="add-ev-file" type="file" accept="image/*,application/pdf" onChange={e => setFile(e.target.files?.[0] || null)} style={{ display: 'none' }} />
        </label>
        {!online && (
          <div data-testid="add-ev-offline-note" style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '10px 12px', background: 'oklch(0.72 0.12 82 / 0.16)', border: `1px solid oklch(0.72 0.12 82 / 0.4)`, borderRadius: 12, marginBottom: 14 }}>
            <Icon.shield size={15} stroke={PS.warn} />
            <div style={{ fontSize: 11.5, color: PS.text, lineHeight: 1.5 }}>ขณะนี้<b>ออฟไลน์</b> — บันทึกจะถูกเก็บในเครื่อง (พร้อมเวลาที่บันทึกจริง) แล้ว<b>ลงนาม seal อัตโนมัติ</b>เมื่อกลับมาออนไลน์</div>
          </div>
        )}
        <button data-testid="add-ev-submit" disabled={busy || !title} onClick={submit} className="ps-press" style={{ width: '100%', height: 50, background: PS.ink, color: PS.page, border: 'none', borderRadius: 14, fontSize: 14, fontWeight: 600, fontFamily: PS.fontUI, opacity: busy || !title ? 0.6 : 1 }}>
          {busy ? 'กำลังบันทึก…' : online ? 'บันทึก + seal ด้วย SHA-256' : 'บันทึกออฟไลน์ (sync ภายหลัง)'}
        </button>
      </div>
    </div>
  );
}

export default function EvidenceVault() {
  const { id } = useParams();
  const nav = useNavigate();
  const { data: events, reload } = useFetch(`/circles/${id}/evidence`, [id]);
  const { data: stats } = useFetch(`/circles/${id}/evidence/stats`, [id]);
  const { data: circle } = useFetch(`/circles/${id}`, [id]);
  const [fi, setFi] = useState(0);
  const [adding, setAdding] = useState(false);

  const shown = useMemo(() => {
    const f = FILTERS[fi];
    if (!f.types) return events || [];
    return (events || []).filter(e => f.types.includes(e.event_type));
  }, [events, fi]);

  return (
    <Screen bg={PS.page}>
      {adding && <AddEvidence circleId={id} onClose={() => setAdding(false)} onDone={(r) => { setAdding(false); if (r?.queued) { notifyInfo('บันทึกออฟไลน์แล้ว — จะ sync และลงนามอัตโนมัติเมื่อกลับมาออนไลน์'); } else { reload(); } }} />}
      <div style={{ padding: '14px 20px 10px', flexShrink: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <button data-testid="vault-back-btn" onClick={() => nav(-1)} className="ps-press" style={{ border: 'none', background: 'transparent', padding: 0 }}><Icon.chevronL size={20} stroke={PS.ink} /></button>
            <div>
              <div style={{ fontSize: 10.5, color: PS.textMuted, letterSpacing: 1, textTransform: 'uppercase' }}>ห้องนิรภัยหลักฐาน</div>
              <div style={{ fontSize: 20, fontWeight: 600, letterSpacing: -0.3 }}>Evidence Vault</div>
            </div>
          </div>
          <button data-testid="vault-dispute-btn" onClick={() => nav(`/app/circle/${id}/dispute`)} className="ps-press" style={{ padding: '6px 12px', borderRadius: 10, border: `1px solid ${PS.line}`, background: 'transparent', fontSize: 11.5, display: 'flex', gap: 6, alignItems: 'center', fontFamily: PS.fontUI }}>
            <Icon.doc size={13} stroke={PS.ink} />ขอสำเนา
          </button>
        </div>
      </div>

      {/* Vault stats */}
      <div className="ps-fade-up" style={{ margin: '8px 16px 14px', padding: 18, background: PS.ink, color: PS.page, borderRadius: 20, flexShrink: 0 }}>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <Icon.shield size={14} stroke="oklch(0.82 0.08 155)" />
          <div style={{ fontSize: 10.5, letterSpacing: 1, textTransform: 'uppercase', color: 'rgba(245,241,234,0.6)' }}>{circle?.name || '—'}</div>
        </div>
        <div style={{ display: 'flex', gap: 20, marginTop: 14, alignItems: 'baseline' }}>
          <div><div className="num" style={{ fontSize: 36, fontWeight: 500, lineHeight: 1, letterSpacing: -0.8 }}>{stats?.event_count ?? '—'}</div><div style={{ fontSize: 10.5, color: 'rgba(245,241,234,0.6)', letterSpacing: 0.8, textTransform: 'uppercase', marginTop: 4 }}>event ที่บันทึก</div></div>
          <div style={{ width: 1, height: 44, background: 'rgba(245,241,234,0.15)' }} />
          <div><div className="num" style={{ fontSize: 20, fontWeight: 500 }}>{thb(stats?.total_transferred || 0)}</div><div style={{ fontSize: 10.5, color: 'rgba(245,241,234,0.6)', letterSpacing: 0.8, textTransform: 'uppercase', marginTop: 4 }}>ยอดที่บันทึก</div></div>
        </div>
        <div style={{ marginTop: 14, padding: 10, background: 'rgba(245,241,234,0.08)', borderRadius: 10, display: 'flex', gap: 8, alignItems: 'center' }}>
          <Icon.lock size={13} stroke="rgba(245,241,234,0.7)" />
          <div className="mono" style={{ fontSize: 10, color: 'rgba(245,241,234,0.75)', flex: 1 }}>CA {stats?.ca_public_key} · SHA-256 + ed25519 — Peershare แก้ไขไม่ได้</div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 6, padding: '0 16px 12px', overflowX: 'auto', flexShrink: 0 }}>
        {FILTERS.map((f, i) => (
          <div key={f.label} data-testid={`vault-filter-${i}`} onClick={() => setFi(i)} className="ps-press" style={{ padding: '5px 12px', borderRadius: 999, flexShrink: 0, background: i === fi ? PS.ink : 'transparent', color: i === fi ? PS.page : PS.text, border: i === fi ? 'none' : `1px solid ${PS.line}`, fontSize: 11.5, fontWeight: 500 }}>{f.label}</div>
        ))}
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '0 16px 90px' }}>
        {shown.map((ev, i) => {
          const IcoC = Icon[ev.icon] || Icon.doc;
          return (
            <div key={ev.event_id} className="ps-fade" style={{ display: 'flex', gap: 12, paddingTop: i === 0 ? 0 : 12 }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 4 }}>
                <div style={{ width: 28, height: 28, borderRadius: 14, background: PS.surface2, border: `1px solid ${PS.line}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><IcoC size={14} stroke={PS.ink} /></div>
                {i < shown.length - 1 && <div style={{ flex: 1, width: 1, background: PS.line, marginTop: 4 }} />}
              </div>
              <div style={{ flex: 1, paddingBottom: 14, borderBottom: i < shown.length - 1 ? `1px solid ${PS.lineSoft}` : 'none' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{ev.title}</div>
                  <div className="mono" style={{ fontSize: 10.5, color: PS.textMuted, flexShrink: 0 }}>{ev.display_time}</div>
                </div>
                <div style={{ fontSize: 11.5, color: PS.textMuted, marginTop: 3 }}>{ev.meta}</div>
                {ev.file && (ev.file.content_type || '').startsWith('image/') && (
                  <img data-testid={`vault-thumb-${ev.event_id}`} alt="หลักฐาน" src={`${BACKEND_URL}/api/files/${ev.file.storage_path}`}
                    style={{ marginTop: 8, maxWidth: 160, maxHeight: 120, borderRadius: 10, border: `1px solid ${PS.line}`, objectFit: 'cover', display: 'block' }} />
                )}
                <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                  <div className="mono" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 8px', background: PS.panel, borderRadius: 6, fontSize: 9.5, color: PS.textMuted }}><Icon.lock size={10} stroke={PS.textMuted} />{ev.hash_short}</div>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 8px', background: 'oklch(0.78 0.06 155 / 0.3)', borderRadius: 6, fontSize: 10, color: 'oklch(0.32 0.08 155)', fontWeight: 600 }}>✓ เซ็น · {ev.signed_by}</div>
                  {ev.gps && <div className="mono" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 8px', background: PS.panel, borderRadius: 6, fontSize: 9.5, color: PS.textMuted }}><Icon.pin size={10} stroke={PS.textMuted} />{ev.gps}</div>}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ position: 'absolute', bottom: 20, left: 0, right: 0, display: 'flex', justifyContent: 'center' }}>
        <button data-testid="vault-add-btn" onClick={() => setAdding(true)} className="ps-press" style={{ background: PS.ink, color: PS.page, border: 'none', borderRadius: 999, padding: '13px 22px', fontSize: 13.5, fontWeight: 600, fontFamily: PS.fontUI, display: 'flex', gap: 8, alignItems: 'center', boxShadow: '0 8px 24px rgba(14,26,43,0.25)' }}>
          <Icon.plus size={16} stroke={PS.page} />เพิ่มหลักฐาน
        </button>
      </div>
    </Screen>
  );
}
